import { Alert, Divider, Modal, Spin } from "antd";
import cx from "classnames";
import _ from "lodash";
import React, {
  PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useProject } from "../../../App/state";
import { useNotification } from "../../../contexts/Notifications";
import { useCreateSheetFloorplanMappingMutation } from "../../../generated/types";
import { useKeyEvent } from "../../../pages/SpatialConfigModePage/hooks/useKeyEvent";
import { DrawingTool } from "../../DrawingTool/DrawingTool";
import {
  STROKE_SIZE_RATIO,
  euclidianDistance,
} from "../../DrawingTool/drawing-utils";
import { Crosshair } from "../../DrawingTool/shapes";
import { CrosshairNode } from "../../DrawingTool/types";
import { FileUploader } from "../../FileUploader";
import { Drawing } from "../../SpatialConfigurationMode/types";
import { BimFloorModel, FloorplanCreateInput } from "../hooks/useFloorplan";
import styles from "./ImportFloorplanModal.module.css";
import { ImportFloorplanModalHeader } from "./ImportFloorplanModalHeader";
import { OpenSpaceSheetImportPanel } from "./OpenSpaceSheetImportPanel";
import { InputRow, SetPointRow } from "./SetPointRow";
import { useOpenSpaceSheetImport } from "./useOpenSpaceSheetImport";

const IMPORT_FLOORPLAN_TEXT = "Import floor plan";
const IMPORT_FLOORPLAN_KEY_MODIFIER = "e";

const PROPERTIES_TITLE = "Properties";
const FLOORPLAN_TITLE = "Floorplan";
const BIM_CONFIGURATION_TITLE = "BIM Configuration";

const SCALE_DISTANCE = "Scale Distance";
const BIM_ORIGIN_X = "BIM Origin X";
const BIM_ORIGIN_Y = "BIM Origin Y";
const BIM_ORIGIN_Z = "BIM Origin Z";
const FLOOR_HEIGHT = "Floor Height";
const ANGLE_TO_TRUE_NORTH = "Angle to True North";
const BIM_PROJECT_ID = "BIM Project ID";

const ORIGIN_TOOLTIP_TEXT =
  "Click “set”, hold “e” on your keyboard, and pick the origin point on the floorplan drawing";
export const BIM_ORIGIN_X_TOOLTIP_TEXT =
  "Enter BIM X Coordinate of the origin point (in mm)";
export const BIM_ORIGIN_Y_TOOLTIP_TEXT =
  "Enter BIM Y Coordinate of the origin point (in mm)";
export const BIM_ORIGIN_Z_TOOLTIP_TEXT =
  "Enter BIM Z Coordinate of the origin point (in mm)";
export const FLOOR_HEIGHT_TOOLTIP_TEXT = "Enter floor height (in mm)";
export const TRUE_NORTH_TOOLTIP_TEXT =
  "Enter the difference between Project North and True North + the difference in the orientation of the drawing compared to the BIM model (if any)";
const SCALE_START_TOOLTIP_TEXT =
  "Find a measurement on the drawing, click “set”, hold “e” on your keyboard, and select one end of it for scale start";
const SCALE_END_TOOLTIP_TEXT =
  "Click “set”, hold “e” on your keyboard, and select another point of the measurement as the scale end";
const SCALE_DISTANCE_TOOLTIP_TEXT =
  "Enter the value between Scale Start and Scale End (in mm)";
const BIM_PROJECT_ID_TOOLTIP_TEXT = "Enter the BIM Project ID (if any)";

const hostname = window.location.hostname;
const regionMatch = hostname.match(/\.([a-z]{2,3})\.disperse\.io/);

const SPECKLE_VIEWER_BASE_URL = `https://speckle.${
  regionMatch?.[1] || "uk"
}.disperse.io`;

export enum InputMode {
  ORIGIN = "Origin",
  SCALE_START = "Scale Start",
  SCALE_END = "Scale End",
}

type DrawingParameters = {
  [InputMode.ORIGIN]: CrosshairNode | undefined;
  [InputMode.SCALE_END]: CrosshairNode | undefined;
  [InputMode.SCALE_START]: CrosshairNode | undefined;
};

const INITIAL_DRAWING_PARAMETERS = {
  [InputMode.ORIGIN]: undefined,
  [InputMode.SCALE_END]: undefined,
  [InputMode.SCALE_START]: undefined,
};

const DRAWING_NODE_COLORS = {
  [InputMode.ORIGIN]: "red",
  [InputMode.SCALE_END]: "green",
  [InputMode.SCALE_START]: "blue",
};

const INPUT_MODE_TOOLTIPS = {
  [InputMode.ORIGIN]: ORIGIN_TOOLTIP_TEXT,
  [InputMode.SCALE_START]: SCALE_START_TOOLTIP_TEXT,
  [InputMode.SCALE_END]: SCALE_END_TOOLTIP_TEXT,
};

const SVG_CIRCLE_SIZE_RATIO = 150;

// Single construction point for the calibration markers so pre-filled and
// hand-picked nodes always render identically.
const makeCrosshairNode = (
  mode: InputMode,
  x: number,
  y: number,
  drawingWidth: number,
): CrosshairNode => ({
  id: mode,
  x,
  y,
  color: DRAWING_NODE_COLORS[mode],
  size: drawingWidth / SVG_CIRCLE_SIZE_RATIO,
  strokeWidth: drawingWidth / SVG_CIRCLE_SIZE_RATIO / STROKE_SIZE_RATIO,
  opacity: 1,
});

// Pre-filled scale points are only trustworthy when the new image is the same
// drawing at a different resolution. When width and height scaled by different
// factors, the sheet extent changed (new border, paper size, revision block)
// and per-axis scaling would place the points on arbitrary content — and a
// transform with ratioX ≠ ratioY isn't representable by one mmScaleFactor.
const PREFILL_ASPECT_RATIO_TOLERANCE = 0.01;

// Calibration of the floor plan being replaced, used to pre-fill the modal
// so the operator doesn't have to re-pick origin and scale from scratch.
export type FloorplanPrefill = {
  originX: number;
  originY: number;
  width: number;
  height: number;
  mmScaleFactor: number;
  scaleStartX?: number | null;
  scaleStartY?: number | null;
  scaleEndX?: number | null;
  scaleEndY?: number | null;
  scaleDistanceMm?: number | null;
  bimOriginX?: number | null;
  bimOriginY?: number | null;
  bimOriginZ?: number | null;
  floorHeight?: number | null;
  angleToTrueNorth?: number | null;
  bimProjectId?: string | null;
  bimFloorModels?: BimFloorModel[] | null;
};

interface ImportFloorplanModalProps {
  onClose: () => void;
  spaceId: string;
  spaceName: string;
  createFloorplan: (floorplan: FloorplanCreateInput) => Promise<string>;
  uploadInProgress: boolean;
  prefill?: FloorplanPrefill;
}

export const ImportFloorplanModal = (props: ImportFloorplanModalProps) => {
  const {
    onClose,
    spaceId,
    spaceName,
    createFloorplan,
    uploadInProgress,
    prefill,
  } = props;
  const isBimEnabled = useMemo(() => true, []);
  const notify = useNotification();
  const project = useProject();
  const [createSheetFloorplanMapping] =
    useCreateSheetFloorplanMappingMutation();
  const [drawing, setDrawing] = useState<Drawing>();
  const [imageFile, setImageFile] = useState<File>();

  const [inputMode, setInputMode] = useState<InputMode>();
  const [drawingParameters, setDrawingParameters] = useState<DrawingParameters>(
    INITIAL_DRAWING_PARAMETERS,
  );
  const [scaleDistance, setScaleDistance] = useState<number | undefined>(
    prefill?.scaleDistanceMm ?? undefined,
  );

  const [bimOriginX, setBimOriginX] = useState<number | undefined>(
    prefill?.bimOriginX ?? undefined,
  );
  const [bimOriginY, setBimOriginY] = useState<number | undefined>(
    prefill?.bimOriginY ?? undefined,
  );
  const [bimOriginZ, setBimOriginZ] = useState<number | undefined>(
    prefill?.bimOriginZ ?? undefined,
  );
  const [floorHeight, setFloorHeight] = useState<number | undefined>(
    prefill?.floorHeight ?? undefined,
  );
  const [angleToTrueNorth, setAngleToTrueNorth] = useState<number | undefined>(
    prefill?.angleToTrueNorth ?? undefined,
  );
  const [bimProjectId, setBimProjectId] = useState<string | undefined>(
    prefill?.bimProjectId ?? undefined,
  );
  const { keyEvent, onKeyUp, onKeyDown } = useKeyEvent();
  const prefillApplied = useRef(false);
  // Why the prefill did or didn't place markers, so the panel can show the
  // previous values as reference when they couldn't be mapped onto the image.
  const [prefillOutcome, setPrefillOutcome] = useState<
    "applied" | "aspectMismatch" | "noStoredPoints"
  >();

  // Once the new image is loaded, seed origin and scale from the previous
  // calibration, scaled in case the new image has different dimensions.
  useEffect(() => {
    if (!drawing || !prefill || prefillApplied.current) {
      return;
    }
    prefillApplied.current = true;
    const ratioX = prefill.width > 0 ? drawing.width / prefill.width : 1;
    const ratioY = prefill.height > 0 ? drawing.height / prefill.height : 1;
    const aspectRatioPreserved =
      Math.abs(ratioX - ratioY) <=
      PREFILL_ASPECT_RATIO_TOLERANCE * Math.max(ratioX, ratioY);
    if (!aspectRatioPreserved) {
      // The scaled positions are untrustworthy for the origin as much as for
      // the scale points, and the seeded distance belongs to a measurement the
      // operator won't be re-drawing — drop all of it rather than pre-fill
      // something authoritative-looking but wrong. The previous values stay
      // visible as reference in the properties panel.
      setScaleDistance(undefined);
      setPrefillOutcome("aspectMismatch");
      notify(
        "The new image's proportions differ from the previous floor plan, so its calibration was not pre-filled. Use the previous values shown in the panel as reference and set the origin and scale manually.",
        "info",
      );
      return;
    }
    const makeNode = (mode: InputMode, x: number, y: number): CrosshairNode =>
      makeCrosshairNode(mode, x * ratioX, y * ratioY, drawing.width);
    const { scaleStartX, scaleStartY, scaleEndX, scaleEndY } = prefill;
    const hasScalePoints =
      scaleStartX != null &&
      scaleStartY != null &&
      scaleEndX != null &&
      scaleEndY != null;
    if (!hasScalePoints) {
      // A distance without its points would pair the old measurement with
      // whatever segment the operator draws next.
      setScaleDistance(undefined);
    }
    setDrawingParameters((prev) => ({
      ...prev,
      [InputMode.ORIGIN]: makeNode(
        InputMode.ORIGIN,
        prefill.originX,
        prefill.originY,
      ),
      ...(hasScalePoints
        ? {
            [InputMode.SCALE_START]: makeNode(
              InputMode.SCALE_START,
              scaleStartX,
              scaleStartY,
            ),
            [InputMode.SCALE_END]: makeNode(
              InputMode.SCALE_END,
              scaleEndX,
              scaleEndY,
            ),
          }
        : {}),
    }));
    setPrefillOutcome(hasScalePoints ? "applied" : "noStoredPoints");
    notify(
      hasScalePoints
        ? "Origin and scale points pre-filled from the previous floor plan. Verify they match the new drawing before confirming."
        : "Origin pre-filled from the previous floor plan. Scale points were not stored for it, so set them manually.",
      "info",
    );
  }, [drawing, prefill, notify]);

  // Shown next to the previous floor plan's factor so a replacement that would
  // change the scale is visible before confirming.
  const newScaleFactor = useMemo(() => {
    const scaleStart = drawingParameters[InputMode.SCALE_START];
    const scaleEnd = drawingParameters[InputMode.SCALE_END];
    if (!scaleStart || !scaleEnd || !scaleDistance) {
      return undefined;
    }
    const pixelDistance = euclidianDistance(
      { x: scaleStart.x, y: scaleStart.y },
      { x: scaleEnd.x, y: scaleEnd.y },
    );
    // Coincident points would make the factor Infinity.
    return pixelDistance > 0 ? scaleDistance / pixelDistance : undefined;
  }, [drawingParameters, scaleDistance]);

  // Gate Confirm on a complete, valid calibration instead of warning after the
  // click: origin plus two distinct scale points and a usable distance.
  const canConfirm =
    drawing !== undefined &&
    imageFile !== undefined &&
    drawingParameters[InputMode.ORIGIN] !== undefined &&
    newScaleFactor !== undefined;

  const handleDrawingClick = (x: number, y: number) => {
    if (inputMode && drawing && keyEvent === IMPORT_FLOORPLAN_KEY_MODIFIER) {
      setDrawingParameters((prev) => ({
        ...prev,
        [inputMode]: makeCrosshairNode(inputMode, x, y, drawing.width),
      }));
    }
  };

  const loadImageFile = useCallback(
    (file: File) => {
      // Defensive reset: the UI currently only allows one image per modal
      // mount, but if a second load ever becomes possible, stale calibration
      // scaled to the first image must not survive onto the new one.
      prefillApplied.current = false;
      setPrefillOutcome(undefined);
      setDrawingParameters(INITIAL_DRAWING_PARAMETERS);
      setScaleDistance(prefill?.scaleDistanceMm ?? undefined);
      setImageFile(file);
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = (e) => {
        if (typeof e.target?.result === "string") {
          const img = new Image();
          img.src = e.target.result;
          img.onload = () => {
            setDrawing({
              url: img.src,
              originX: 0,
              originY: 0,
              mmScaleFactor: 1,
              width: img.width,
              height: img.height,
            });
          };
        }
      };
    },
    [prefill],
  );

  const {
    selectedRegion,
    selectedOrganization,
    selectedSite,
    selectedSiteSheet,
    organizations,
    sites,
    siteSheets,
    openSpaceError,
    openSpaceLoading,
    orgLoading,
    siteLoading,
    sheetLoading,
    handleRegionChange,
    handleOrganizationChange,
    handleSiteChange,
    handleSheetChange,
    handleOpenSpaceImport,
  } = useOpenSpaceSheetImport({ loadImageFile, notify });

  const onConfirm = useCallback(async () => {
    const scaleStart = drawingParameters[InputMode.SCALE_START];
    const scaleEnd = drawingParameters[InputMode.SCALE_END];
    const origin = drawingParameters[InputMode.ORIGIN];

    if (
      scaleStart &&
      scaleEnd &&
      scaleDistance &&
      newScaleFactor === undefined
    ) {
      notify(
        "Scale Start and Scale End must be two different points",
        "warning",
      );
      return;
    }
    if (
      scaleStart &&
      scaleEnd &&
      origin &&
      newScaleFactor !== undefined &&
      scaleDistance &&
      drawing &&
      imageFile
    ) {
      const floorplan = {
        spaceId: spaceId,
        originX: origin.x,
        originY: origin.y,
        width: drawing.width,
        height: drawing.height,
        // The same computation shown as "New scale" — what the operator
        // verified is what gets persisted.
        mmScaleFactor: newScaleFactor,
        scaleStartX: scaleStart.x,
        scaleStartY: scaleStart.y,
        scaleEndX: scaleEnd.x,
        scaleEndY: scaleEnd.y,
        scaleDistanceMm: scaleDistance,
        floorplanFile: imageFile,
        bimOriginX,
        bimOriginY,
        bimOriginZ: bimOriginZ || 0,
        floorHeight,
        angleToTrueNorth,
        bimProjectId,
        // Not editable here; carried over so replacing the image doesn't
        // wipe the BIM floor models (patch overwrites them when absent).
        bimFloorModels: prefill?.bimFloorModels,
      };
      const floorplanId = await createFloorplan(floorplan);
      if (selectedSiteSheet) {
        const sheet = siteSheets.find((s) => s.id === selectedSiteSheet);
        try {
          await createSheetFloorplanMapping({
            variables: {
              customer: project.customer,
              project: project.project,
              scope: project.scope,
              floorplanId,
              osSheetId: selectedSiteSheet,
              imageId: sheet?.imageId,
              siteId: selectedSite,
            },
          });
        } catch (error) {
          console.error("Error mapping floorplan to OpenSpace sheet:", error);
          notify(
            "Floor plan created but failed to map it to the selected OpenSpace sheet.",
            "warning",
          );
        }
      }
    } else {
      notify("Please set all parameters", "warning");
    }
  }, [
    drawingParameters,
    scaleDistance,
    drawing,
    imageFile,
    spaceId,
    bimOriginX,
    bimOriginY,
    bimOriginZ,
    floorHeight,
    angleToTrueNorth,
    bimProjectId,
    prefill,
    newScaleFactor,
    createFloorplan,
    createSheetFloorplanMapping,
    notify,
    project.customer,
    project.project,
    project.scope,
    selectedSite,
    selectedSiteSheet,
    siteSheets,
  ]);

  const onImport = useCallback(
    (acceptedFiles) => {
      if (acceptedFiles.length !== 1) {
        notify(
          "Upload failed, we currently only support uploading one file at a time",
        );
      } else {
        const file = acceptedFiles[0];
        loadImageFile(file);
      }
    },
    [notify, loadImageFile],
  );

  const nodes: CrosshairNode[] = useMemo(
    () => _.compact(Object.values(drawingParameters)),
    [drawingParameters],
  );

  const previewUrl = `${SPECKLE_VIEWER_BASE_URL}/projects/${
    encodeURIComponent(bimProjectId ?? "") || "<project-id>"
  }/models/"}`;

  return (
    <div onKeyPress={onKeyDown} onKeyUp={onKeyUp} tabIndex={0}>
      <Modal
        bodyStyle={{ height: "80vh" }}
        width={"90vw"}
        title={<ImportFloorplanModalHeader floorName={spaceName} />}
        closable={false}
        visible={true}
        onCancel={onClose}
        destroyOnClose={true}
        onOk={onConfirm}
        okText={"Confirm"}
        okButtonProps={{ disabled: !canConfirm }}
      >
        <Spin spinning={uploadInProgress} tip="Upload in progress...">
          {drawing ? (
            <div className={styles["modal-body"]}>
              <div className={styles["drawing-tool"]}>
                <DrawingTool
                  nodes={nodes.map((node) => (
                    <Crosshair key={node.id} node={node} />
                  ))}
                  drawing={drawing}
                  onDrawingClick={handleDrawingClick}
                />
              </div>
              <div className={styles["set-point-box"]}>
                <div
                  className={cx(styles["text__centred"], styles["text__title"])}
                >
                  {PROPERTIES_TITLE}
                </div>
                <GroupedProperties title={FLOORPLAN_TITLE}>
                  {Object.values(InputMode).map((mode, index) => (
                    <SetPointRow
                      key={index}
                      tooltipText={INPUT_MODE_TOOLTIPS[mode]}
                      inputMode={mode}
                      value={drawingParameters[mode]}
                      onSet={setInputMode}
                      activeInputMode={inputMode}
                    />
                  ))}
                  <InputRow
                    name={SCALE_DISTANCE}
                    value={scaleDistance}
                    onSet={setScaleDistance}
                    unit={"mm"}
                    tooltipText={SCALE_DISTANCE_TOOLTIP_TEXT}
                  />
                  {prefill && (
                    <div
                      className={styles["text__centred"]}
                      style={{ marginTop: "8px" }}
                    >
                      Previous scale: {prefill.mmScaleFactor.toFixed(3)} mm/px
                      {newScaleFactor !== undefined &&
                        ` · New scale: ${newScaleFactor.toFixed(3)} mm/px`}
                    </div>
                  )}
                  {prefill && prefillOutcome === "aspectMismatch" && (
                    <>
                      <Alert
                        type="warning"
                        showIcon
                        style={{ marginTop: "8px" }}
                        message="The new image's proportions differ from the previous floor plan, so its calibration points can't be mapped onto this drawing. Select a new origin and scale; the previous values are shown below for reference."
                      />
                      <div
                        className={styles["text__centred"]}
                        style={{ marginTop: "8px" }}
                      >
                        Previous calibration: origin (
                        {prefill.originX.toFixed(1)},{" "}
                        {prefill.originY.toFixed(1)})
                        {prefill.scaleStartX != null &&
                          prefill.scaleStartY != null &&
                          prefill.scaleEndX != null &&
                          prefill.scaleEndY != null &&
                          `, scale (${prefill.scaleStartX.toFixed(
                            1,
                          )}, ${prefill.scaleStartY.toFixed(
                            1,
                          )}) → (${prefill.scaleEndX.toFixed(
                            1,
                          )}, ${prefill.scaleEndY.toFixed(1)})`}
                        {prefill.scaleDistanceMm != null &&
                          ` over ${prefill.scaleDistanceMm} mm`}
                        {` — in the previous image's ${prefill.width}×${prefill.height} px coordinates`}
                      </div>
                    </>
                  )}
                </GroupedProperties>
                {isBimEnabled && (
                  <GroupedProperties title={BIM_CONFIGURATION_TITLE}>
                    <InputRow
                      name={BIM_ORIGIN_X}
                      value={bimOriginX}
                      onSet={setBimOriginX}
                      unit={"mm"}
                      tooltipText={BIM_ORIGIN_X_TOOLTIP_TEXT}
                    />
                    <InputRow
                      name={BIM_ORIGIN_Y}
                      value={bimOriginY}
                      onSet={setBimOriginY}
                      unit={"mm"}
                      tooltipText={BIM_ORIGIN_Y_TOOLTIP_TEXT}
                    />
                    <InputRow
                      name={BIM_ORIGIN_Z}
                      value={bimOriginZ}
                      onSet={setBimOriginZ}
                      unit={"mm"}
                      tooltipText={BIM_ORIGIN_Z_TOOLTIP_TEXT}
                    />
                    <InputRow
                      name={FLOOR_HEIGHT}
                      value={floorHeight}
                      onSet={setFloorHeight}
                      unit={"mm"}
                      tooltipText={FLOOR_HEIGHT_TOOLTIP_TEXT}
                    />
                    <InputRow
                      name={ANGLE_TO_TRUE_NORTH}
                      value={angleToTrueNorth}
                      onSet={setAngleToTrueNorth}
                      unit={"degrees"}
                      tooltipText={TRUE_NORTH_TOOLTIP_TEXT}
                    />
                    <InputRow
                      name={BIM_PROJECT_ID}
                      value={bimProjectId}
                      onSet={setBimProjectId}
                      unit={""}
                      tooltipText={BIM_PROJECT_ID_TOOLTIP_TEXT}
                      type="string"
                    />
                    <div
                      style={{
                        marginTop: "16px",
                        padding: "8px",
                        backgroundColor: "#f0f0f0",
                        borderRadius: "4px",
                      }}
                    >
                      <strong>Preview URL:</strong>
                      <div>
                        <a
                          href={previewUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {previewUrl}
                        </a>
                      </div>
                    </div>
                  </GroupedProperties>
                )}
              </div>
            </div>
          ) : (
            <div className={styles["import-options"]}>
              <div className={styles["import-column"]}>
                <div className={styles["import-column-title"]}>
                  Upload Floorplan
                </div>
                <div className={styles["import-column-body"]}>
                  <FileUploader
                    title={IMPORT_FLOORPLAN_TEXT}
                    onFilesSelected={onImport}
                  />
                </div>
              </div>
              <div className={styles["import-divider"]} />
              <OpenSpaceSheetImportPanel
                selectedRegion={selectedRegion}
                selectedOrganization={selectedOrganization}
                selectedSite={selectedSite}
                selectedSiteSheet={selectedSiteSheet}
                organizations={organizations}
                sites={sites}
                siteSheets={siteSheets}
                orgLoading={orgLoading}
                siteLoading={siteLoading}
                sheetLoading={sheetLoading}
                openSpaceLoading={openSpaceLoading}
                openSpaceError={openSpaceError}
                onRegionChange={handleRegionChange}
                onOrganizationChange={handleOrganizationChange}
                onSiteChange={handleSiteChange}
                onSheetChange={handleSheetChange}
                onUseSheet={handleOpenSpaceImport}
              />
            </div>
          )}
        </Spin>
      </Modal>
    </div>
  );
};

const GroupedProperties: React.FC<PropsWithChildren<{ title: string }>> = ({
  title,
  children,
}) => (
  <React.Fragment>
    <Divider>
      <div className={cx(styles["text__centred"], styles["text__subtitle"])}>
        {title}
      </div>
    </Divider>
    {children}
  </React.Fragment>
);
